import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../config/api';
import * as XLSX from 'xlsx';
import './GestionProducto.css';

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const formatearFecha = (fecha) => {
    if (!fecha) return '';
    const [year, month, day] = fecha.split('-');
    return `${day}/${month}/${year}`;
};

const formatearNumero = (valor, decimales = 0) => {
    if (valor == null) return '0';
    return Number(valor).toLocaleString('es-PY', { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
};

function FNCredito({ usuario }) {
    const [ncreditos, setNcreditos] = useState([]);
    const [ncreditosFiltrados, setNcreditosFiltrados] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [modalAbierto, setModalAbierto] = useState(false);

    // Detalle (items en memoria antes de guardar)
    const [detalles, setDetalles] = useState([]);
    const [productoSeleccionado, setProductoSeleccionado] = useState(null);
    const [detalleTemp, setDetalleTemp] = useState({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, idPresent: 'UN' });

    // Catalogos
    const [timbradosVigentes, setTimbradosVigentes] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [vendedores, setVendedores] = useState([]);
    const [monedas, setMonedas] = useState([]);
    const [depositos, setDepositos] = useState([]);
    const [listasPrecios, setListasPrecios] = useState([]);
    const [sucursales, setSucursales] = useState([]);

    // Selectores activos
    const [timbradoSeleccionado, setTimbradoSeleccionado] = useState(null);
    const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
    const [vendedorSeleccionado, setVendedorSeleccionado] = useState(null);
    const [dropdownTimbradoAbierto, setDropdownTimbradoAbierto] = useState(false);
    const [dropdownClienteAbierto, setDropdownClienteAbierto] = useState(false);
    const [dropdownVendedorAbierto, setDropdownVendedorAbierto] = useState(false);
    const [dropdownProductoAbierto, setDropdownProductoAbierto] = useState(false);
    const [busquedaTimbrado, setBusquedaTimbrado] = useState('');
    const [busquedaCliente, setBusquedaCliente] = useState('');
    const [busquedaVendedor, setBusquedaVendedor] = useState('');
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);

    const dropdownTimbradoRef = useRef(null);
    const dropdownClienteRef = useRef(null);
    const dropdownVendedorRef = useRef(null);
    const dropdownProductoRef = useRef(null);

    const [formulario, setFormulario] = useState({
        idSucursal: '', timbrado: '', boca: 1,
        idCliente: '', nomCliente: '', rucCliente: '',
        fecha: obtenerFechaHoy(),
        idVendedor: '',
        idLisprecio: '', idMoneda: '', cambio: 1,
        idDeposito: 1, obs: '', tipoNotacr: '',
        origen: 1, estado: 'I', comprobante: ''
    });

    // Modal Ver
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [ncreditoVer, setNcreditoVer] = useState(null);
    const [detallesVer, setDetallesVer] = useState([]);

    // Paginacion
    const [paginaActual, setPaginaActual] = useState(1);
    const [itemsPorPagina, setItemsPorPagina] = useState(20);

    useEffect(() => { cargarNcreditos(); cargarDatosSelectores(); }, []);
    useEffect(() => { filtrarNcreditos(); }, [busqueda, ncreditos]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownTimbradoRef.current && !dropdownTimbradoRef.current.contains(event.target)) setDropdownTimbradoAbierto(false);
            if (dropdownClienteRef.current && !dropdownClienteRef.current.contains(event.target)) setDropdownClienteAbierto(false);
            if (dropdownVendedorRef.current && !dropdownVendedorRef.current.contains(event.target)) setDropdownVendedorAbierto(false);
            if (dropdownProductoRef.current && !dropdownProductoRef.current.contains(event.target)) setDropdownProductoAbierto(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const cargarNcreditos = async () => {
        try {
            const r = await axios.get(getApiUrl('/api/ncredito'));
            setNcreditos(r.data);
        } catch (e) { console.error(e); alert('Error al cargar notas de credito'); }
    };

    const cargarDatosSelectores = async () => {
        try {
            const [r1, r2, r3, r4, r5, r6, r7] = await Promise.all([
                axios.get(getApiUrl('/api/ncredito/timbrados-vigentes')),
                axios.get(getApiUrl('/api/clientes')),
                axios.get(getApiUrl('/api/vendedores')),
                axios.get(getApiUrl('/api/monedas')),
                axios.get(getApiUrl('/api/depositos')),
                axios.get(getApiUrl('/api/listas-precio')),
                axios.get(getApiUrl('/api/sucursales/activas'))
            ]);
            setTimbradosVigentes(r1.data); setClientes(r2.data); setVendedores(r3.data);
            setMonedas(r4.data); setDepositos(r5.data); setListasPrecios(r6.data);
            setSucursales(r7.data);
        } catch (e) { console.error('Error selectores:', e); }
    };

    const filtrarNcreditos = () => {
        if (!busqueda) { setNcreditosFiltrados(ncreditos); setPaginaActual(1); return; }
        setNcreditosFiltrados(ncreditos.filter(n =>
            n.notacredito?.toString().includes(busqueda) ||
            n.nomCliente?.toLowerCase().includes(busqueda.toLowerCase()) ||
            n.rucCliente?.toLowerCase().includes(busqueda.toLowerCase()) ||
            n.timbrado?.includes(busqueda)
        ));
        setPaginaActual(1);
    };

    // Selectores
    // Timbrados filtrados por sucursal seleccionada (si hay una)
    const filtrarTimbrados = (b) => {
        let lista = timbradosVigentes;
        if (formulario.idSucursal) {
            lista = lista.filter(t => Number(t.idSucursal) === Number(formulario.idSucursal));
        }
        return !b ? lista : lista.filter(t => t.timbrado?.includes(b));
    };
    const seleccionarTimbrado = (t) => {
        setTimbradoSeleccionado(t);
        setFormulario(f => ({ ...f, timbrado: t.timbrado, idSucursal: t.idSucursal, boca: t.boca }));
        setBusquedaTimbrado(''); setDropdownTimbradoAbierto(false);
    };

    const seleccionarSucursal = (idSucursal) => {
        // Si cambia la sucursal y hay timbrado seleccionado de otra sucursal, limpiar timbrado
        setFormulario(f => {
            if (timbradoSeleccionado && Number(timbradoSeleccionado.idSucursal) !== Number(idSucursal)) {
                setTimbradoSeleccionado(null);
                return { ...f, idSucursal, timbrado: '', boca: 1 };
            }
            return { ...f, idSucursal };
        });
    };

    const filtrarClientes = (b) => !b ? clientes.slice(0, 50) : clientes.filter(c => c.idCliente?.toString().includes(b) || c.nomCliente?.toLowerCase().includes(b.toLowerCase()) || c.rucCliente?.toLowerCase().includes(b.toLowerCase()));
    const seleccionarCliente = (c) => {
        setClienteSeleccionado(c);
        setFormulario(f => ({
            ...f,
            idCliente: c.idCliente,
            nomCliente: c.nomCliente || '',
            rucCliente: c.rucCliente || '',
            idVendedor: c.idVendedor || f.idVendedor,
            idLisprecio: c.idLisprecio || f.idLisprecio
        }));
        if (c.idVendedor) { const v = vendedores.find(v => v.idVendedor === c.idVendedor); if (v) setVendedorSeleccionado(v); }
        setBusquedaCliente(''); setDropdownClienteAbierto(false);
    };

    const filtrarVendedores = (b) => !b ? vendedores.slice(0, 50) : vendedores.filter(v => v.idVendedor?.toString().includes(b) || v.nomVendedor?.toLowerCase().includes(b.toLowerCase()));
    const seleccionarVendedor = (v) => { setVendedorSeleccionado(v); setFormulario(f => ({...f, idVendedor: v.idVendedor})); setBusquedaVendedor(''); setDropdownVendedorAbierto(false); };

    // Detalle - calculo IVA (precio incluye IVA)
    const calcularLinea = (cant, precio, descPct, gravada) => {
        const subtotal = precio * cant;
        const descuento = subtotal * (descPct / 100);
        const total = subtotal - descuento;
        let monGravada = 0, monIva = 0, monExenta = 0, monGravada5 = 0, monGravada10 = 0;
        if (gravada === 10) { monGravada = total / 1.10; monIva = total - monGravada; monGravada10 = Math.round(monGravada); }
        else if (gravada === 5) { monGravada = total / 1.05; monIva = total - monGravada; monGravada5 = Math.round(monGravada); }
        else { monExenta = total; }
        return { monGravada: Math.round(monGravada), monIva: Math.round(monIva), monExenta: Math.round(monExenta), monGravada5, monGravada10, total: Math.round(total), descuento: Math.round(descuento) };
    };

    // Busqueda de productos: se espera 250 ms tras la ultima tecla y solo se muestra la respuesta
    // de la ultima busqueda (una respuesta lenta anterior no pisa a la mas reciente).
    const busquedaProductoRef = useRef({ timer: null, secuencia: 0 });
    const buscarProductoAPI = (texto) => {
        const ref = busquedaProductoRef.current;
        clearTimeout(ref.timer);
        if (texto.trim().length < 2) { ref.secuencia++; setProductosEncontrados([]); return; }
        const seq = ++ref.secuencia;
        ref.timer = setTimeout(async () => {
            try {
                const r = await axios.get(getApiUrl('/api/ncredito/buscar-producto'), { params: { busqueda: texto } });
                if (seq === ref.secuencia) setProductosEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
    };

    const seleccionarProductoDetalle = async (producto) => {
        setProductoSeleccionado(producto); setProductosEncontrados([]); setDropdownProductoAbierto(false);
        setBusquedaProducto(producto.descripcion);
        let precioConIva = Number(producto.precio) || 0;
        let gravada = Number(producto.gravada) || 0;
        const idLisprecio = formulario.idLisprecio;
        if (idLisprecio) {
            try {
                const r = await axios.get(getApiUrl('/api/ncredito/precio'), { params: { idLisprecio, idProducto: producto.id_producto } });
                precioConIva = Number(r.data.precio) || precioConIva;
                gravada = Number(r.data.gravada) || gravada;
            } catch (e) { /* usar precio base */ }
        }
        setDetalleTemp({ cantidad: 1, precio: precioConIva, porcDescuento: 0, gravada, idPresent: producto.id_present || 'UN' });
    };

    const agregarDetalle = () => {
        if (!productoSeleccionado) { alert('Seleccione un producto'); return; }
        if (detalleTemp.cantidad <= 0) { alert('La cantidad debe ser mayor a 0'); return; }
        if (detalleTemp.precio <= 0) { alert('El precio debe ser mayor a 0'); return; }
        if (detalles.find(d => d.idProducto === productoSeleccionado.id_producto)) { alert('Producto ya agregado'); return; }

        const calc = calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.porcDescuento, detalleTemp.gravada);
        setDetalles(prev => [...prev, {
            idProducto: productoSeleccionado.id_producto,
            nomProducto: productoSeleccionado.descripcion,
            cantidad: detalleTemp.cantidad,
            precio: detalleTemp.precio,
            porcDescuento: detalleTemp.porcDescuento,
            gravada: detalleTemp.gravada,
            monExenta: calc.monExenta, monGravada: calc.monGravada, monIva: calc.monIva,
            monGravada5: calc.monGravada5, monGravada10: calc.monGravada10,
            totalLinea: calc.total,
            idPresent: detalleTemp.idPresent,
            costoprom: Number(productoSeleccionado.costoprom) || 0
        }]);
        setProductoSeleccionado(null); setBusquedaProducto('');
        setDetalleTemp({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, idPresent: 'UN' });
    };

    const eliminarDetalle = (idProducto) => { setDetalles(prev => prev.filter(d => d.idProducto !== idProducto)); };

    const calcularTotales = () => {
        let exenta = 0, gravada = 0, iva = 0;
        detalles.forEach(d => { exenta += d.monExenta; gravada += d.monGravada; iva += d.monIva; });
        return { exenta, gravada, iva, total: exenta + gravada + iva };
    };

    const abrirModalNuevo = () => {
        setFormulario({
            idSucursal: '', timbrado: '', boca: 1,
            idCliente: '', nomCliente: '', rucCliente: '',
            fecha: obtenerFechaHoy(),
            idVendedor: '',
            idLisprecio: '', idMoneda: '', cambio: 1,
            idDeposito: 1, obs: '', tipoNotacr: '',
            origen: 1, estado: 'I', comprobante: ''
        });
        setTimbradoSeleccionado(null); setClienteSeleccionado(null); setVendedorSeleccionado(null);
        setDetalles([]); setProductoSeleccionado(null); setBusquedaProducto('');
        setDetalleTemp({ cantidad: 1, precio: 0, porcDescuento: 0, gravada: 0, idPresent: 'UN' });
        setModalAbierto(true);
    };

    const cerrarModal = () => { setModalAbierto(false); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formulario.idSucursal) { alert('Debe seleccionar una sucursal'); return; }
        if (!formulario.timbrado) { alert('Debe seleccionar un timbrado'); return; }
        if (!formulario.tipoNotacr || !['D', 'C'].includes(formulario.tipoNotacr)) { alert('Debe seleccionar el tipo de NCR (Devolucion o Concepto)'); return; }
        if (!formulario.idCliente) { alert('Debe seleccionar un cliente'); return; }
        if (!formulario.fecha) { alert('Debe ingresar una fecha'); return; }
        if (!formulario.idMoneda) { alert('Debe seleccionar una moneda'); return; }
        if (!formulario.idLisprecio) { alert('Debe seleccionar una lista de precios'); return; }
        if (detalles.length === 0) { alert('Debe agregar al menos un item'); return; }

        try {
            const totales = calcularTotales();
            const cabecera = {
                ...formulario,
                monExenta: totales.exenta,
                monGravada: totales.gravada,
                monIva: totales.iva,
                monTotal: totales.total,
                saldo: totales.total,
                idOperador: usuario || ''
            };
            const response = await axios.post(getApiUrl('/api/ncredito'), cabecera);
            const creada = response.data;
            const nro = creada.notacredito;

            // Insertar items
            for (const d of detalles) {
                await axios.post(getApiUrl('/api/ncredito/detalle'), {
                    idSucursal: formulario.idSucursal,
                    notacredito: nro,
                    timbrado: formulario.timbrado,
                    idProducto: d.idProducto,
                    boca: formulario.boca,
                    idDeposito: formulario.idDeposito,
                    fecha: formulario.fecha,
                    cantidad: d.cantidad,
                    precio: d.precio,
                    monExenta: d.monExenta,
                    monGravada: d.monGravada,
                    monIva: d.monIva,
                    idPresent: d.idPresent || 'UN',
                    costoprom: d.costoprom,
                    cantPresent: 1
                });
            }
            alert('Nota de Credito Nro ' + nro + ' creada exitosamente con ' + detalles.length + ' items');
            cerrarModal();
            cargarNcreditos();
        } catch (error) {
            const msg = error.response?.data || 'Error al crear la nota de credito';
            alert(typeof msg === 'string' ? msg : 'Error al crear la nota de credito');
        }
    };

    const anularNcredito = async (n) => {
        if (n.estado === 'A') { alert('La NCR ya esta anulada'); return; }
        if (!window.confirm('Anular la NCR Nro ' + n.notacredito + '?\nEsta accion no se puede deshacer.')) return;
        try {
            await axios.put(getApiUrl('/api/ncredito/anular'), null, {
                params: { idSucursal: n.idSucursal, notacredito: n.notacredito, timbrado: n.timbrado }
            });
            alert('NCR anulada exitosamente');
            cargarNcreditos();
        } catch (error) {
            const msg = error.response?.data || 'Error al anular';
            alert(typeof msg === 'string' ? msg : 'Error al anular');
        }
    };

    const verNcredito = async (n) => {
        setNcreditoVer(n);
        try {
            const r = await axios.get(getApiUrl('/api/ncredito/detalle'), {
                params: { idSucursal: n.idSucursal, notacredito: n.notacredito, timbrado: n.timbrado }
            });
            setDetallesVer(r.data);
        } catch (e) { setDetallesVer([]); }
        setModalVerAbierto(true);
    };

    const exportarExcel = () => {
        const d = ncreditosFiltrados.map(n => ({
            'Nro': n.notacredito, 'Timbrado': n.timbrado, 'Fecha': n.fecha ? formatearFecha(n.fecha) : '',
            'Cliente': n.nomCliente, 'RUC': n.rucCliente, 'Total': n.monTotal, 'Estado': n.estado
        }));
        const ws = XLSX.utils.json_to_sheet(d); const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'NotasCredito'); XLSX.writeFile(wb, 'notas_credito.xlsx');
    };

    // Paginacion
    const indiceUltimo = paginaActual * itemsPorPagina;
    const indicePrimero = indiceUltimo - itemsPorPagina;
    const ncreditosActuales = ncreditosFiltrados.slice(indicePrimero, indiceUltimo);
    const totalPaginas = Math.ceil(ncreditosFiltrados.length / itemsPorPagina);

    const previewLinea = productoSeleccionado ? calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.porcDescuento, detalleTemp.gravada) : null;
    const totales = calcularTotales();

    return (
        <div className="gestion-producto-container">
            <div className="header-seccion">
                <h2>Notas de Credito</h2>
                <div className="acciones-header">
                    <input type="text" className="input-busqueda" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
                    <button className="btn btn-nuevo" onClick={abrirModalNuevo}>+ Nueva NCR</button>
                    <button className="btn btn-exportar" onClick={exportarExcel}>Exportar Excel</button>
                </div>
            </div>

            {/* Grilla */}
            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead><tr>
                        <th>Numero</th><th>Timbrado</th><th>Tipo</th><th>Fecha</th><th>Cliente</th><th>RUC</th>
                        <th style={{textAlign:'right'}}>Total</th><th style={{textAlign:'right'}}>Saldo</th><th>Moneda</th><th>Estado</th><th>Acc</th>
                    </tr></thead>
                    <tbody>
                        {ncreditosActuales.length === 0 ? (
                            <tr><td colSpan="11" className="sin-datos">No hay notas de credito</td></tr>
                        ) : ncreditosActuales.map((n, i) => (
                            <tr key={i}>
                                <td>{String(n.idSucursal).padStart(3,'0')}-{String(n.boca || 1).padStart(3,'0')}-{n.notacredito}</td>
                                <td>{n.timbrado}</td>
                                <td><span style={{ padding: '2px 6px', borderRadius: '3px', fontSize: '12px', fontWeight: 'bold', backgroundColor: n.tipoNotacr === 'D' ? '#EDF1FB' : n.tipoNotacr === 'C' ? '#fff3cd' : '#f0f0f0', color: n.tipoNotacr === 'D' ? '#001A70' : n.tipoNotacr === 'C' ? '#856404' : '#7f8c8d' }}>{n.tipoNotacr || '-'}</span></td>
                                <td>{n.fecha ? formatearFecha(n.fecha) : ''}</td>
                                <td>{n.nomCliente}</td>
                                <td>{n.rucCliente}</td>
                                <td style={{textAlign:'right'}}>{formatearNumero(n.monTotal)}</td>
                                <td style={{textAlign:'right'}}>{formatearNumero(n.saldo)}</td>
                                <td>{n.idMoneda}</td>
                                <td style={n.estado === 'A' ? { color: '#c62828', fontWeight: 'bold' } : {}}>{n.estado === 'A' ? 'Anulada' : n.estado === 'I' ? 'Activa' : n.estado}</td>
                                <td>
                                    <button type="button" onClick={() => verNcredito(n)} className="btn-accion" title="Ver">👁</button>
                                    <button type="button" onClick={() => anularNcredito(n)} className="btn-accion" title="Anular NCR"
                                        disabled={n.estado === 'A'}
                                        style={n.estado === 'A' ? { opacity: 0.3 } : { color: '#c62828' }}>✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Paginacion */}
            {ncreditosFiltrados.length > 0 && (
                <div className="paginacion-container">
                    <div className="paginacion-info">
                        <span>Mostrando {indicePrimero + 1} a {Math.min(indiceUltimo, ncreditosFiltrados.length)} de {ncreditosFiltrados.length}</span>
                        <select value={itemsPorPagina} onChange={(e) => { setItemsPorPagina(Number(e.target.value)); setPaginaActual(1); }} className="select-registros">
                            <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                        </select>
                    </div>
                    <div className="paginacion-controles">
                        <button onClick={() => setPaginaActual(1)} disabled={paginaActual === 1} className="btn-paginacion">|&lt;</button>
                        <button onClick={() => setPaginaActual(p => p - 1)} disabled={paginaActual === 1} className="btn-paginacion">&lt;</button>
                        <button onClick={() => setPaginaActual(p => p + 1)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;</button>
                        <button onClick={() => setPaginaActual(totalPaginas)} disabled={paginaActual >= totalPaginas} className="btn-paginacion">&gt;|</button>
                    </div>
                </div>
            )}

            {/* MODAL NUEVA NCR */}
            {modalAbierto && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '1100px' }}>
                        <div className="modal-header">
                            <h3>Nueva Nota de Credito</h3>
                            <button onClick={cerrarModal} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            <form onSubmit={handleSubmit}>
                                <div className="form-section">
                                    <h3>Datos de Cabecera</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                                        <div>
                                            <div className="form-group">
                                                <label>Sucursal *</label>
                                                <select value={formulario.idSucursal || ''}
                                                    onChange={(e) => seleccionarSucursal(e.target.value ? Number(e.target.value) : '')}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                    <option value="">-- Seleccione sucursal --</option>
                                                    {sucursales.map(s => (
                                                        <option key={s.idSucursal} value={s.idSucursal}>
                                                            {s.idSucursal} - {s.nombre}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Timbrado *</label>
                                                <div className="cuenta-selector" ref={dropdownTimbradoRef}>
                                                    {timbradoSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{timbradoSeleccionado.timbrado} - NCR (Boca {timbradoSeleccionado.boca})</span>
                                                            <button type="button" onClick={() => { setTimbradoSeleccionado(null); setFormulario(f => ({...f, timbrado:'', boca:1})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar timbrado NCR..." value={busquedaTimbrado}
                                                            onChange={(e) => setBusquedaTimbrado(e.target.value)}
                                                            onFocus={() => setDropdownTimbradoAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownTimbradoAbierto && !timbradoSeleccionado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarTimbrados(busquedaTimbrado).map((t, i) => (
                                                                <div key={i} className="cuenta-item" onClick={() => seleccionarTimbrado(t)}>
                                                                    <strong>{t.timbrado}</strong> | Boca {t.boca} | Rango {t.desde}-{t.hasta} | Ult: {t.ultValor}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Boca y Nro NCR siguiente (readonly) - visibles al seleccionar timbrado */}
                                            {timbradoSeleccionado && (
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                                                    <div className="form-group">
                                                        <label>Boca</label>
                                                        <input type="text"
                                                            value={timbradoSeleccionado.boca || ''}
                                                            readOnly disabled
                                                            className="form-input"
                                                            style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: '#001A70', cursor: 'not-allowed' }} />
                                                    </div>
                                                    <div className="form-group">
                                                        <label>Nro NCR a utilizar</label>
                                                        <input type="text"
                                                            value={(() => {
                                                                const ult = Number(timbradoSeleccionado.ultValor) || 0;
                                                                const desde = Number(timbradoSeleccionado.desde) || 0;
                                                                const proximo = ult + 1;
                                                                return proximo < desde ? desde : proximo;
                                                            })()}
                                                            readOnly disabled
                                                            className="form-input"
                                                            style={{ backgroundColor: '#f0f0f0', fontWeight: 'bold', color: '#2DCCD3', cursor: 'not-allowed' }} />
                                                    </div>
                                                </div>
                                            )}
                                            <div className="form-group">
                                                <label>Cliente *</label>
                                                <div className="cuenta-selector" ref={dropdownClienteRef}>
                                                    {clienteSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{clienteSeleccionado.idCliente} - {clienteSeleccionado.nomCliente}</span>
                                                            <button type="button" onClick={() => { setClienteSeleccionado(null); setFormulario(f => ({...f, idCliente:'', nomCliente:'', rucCliente:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar cliente..." value={busquedaCliente} onChange={(e) => setBusquedaCliente(e.target.value)} onFocus={() => setDropdownClienteAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownClienteAbierto && !clienteSeleccionado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarClientes(busquedaCliente).map(c => (
                                                                <div key={c.idCliente} className="cuenta-item" onClick={() => seleccionarCliente(c)}>
                                                                    <strong>{c.idCliente}</strong> - {c.nomCliente} ({c.rucCliente})
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Fecha *</label>
                                                <input type="date" value={formulario.fecha} onChange={(e) => setFormulario(f => ({...f, fecha: e.target.value}))} className="form-input" />
                                            </div>
                                            <div className="form-group">
                                                <label>Tipo de NCR *</label>
                                                <div style={{ display: 'flex', gap: '15px', padding: '8px 0' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, padding: '8px', border: formulario.tipoNotacr === 'D' ? '2px solid #2DCCD3' : '1px solid #ddd', borderRadius: '5px', backgroundColor: formulario.tipoNotacr === 'D' ? '#EDF1FB' : 'white' }}>
                                                        <input type="radio" name="tipoNotacr" value="D"
                                                            checked={formulario.tipoNotacr === 'D'}
                                                            onChange={(e) => setFormulario(f => ({...f, tipoNotacr: e.target.value}))}
                                                            style={{ marginRight: '8px' }} />
                                                        <div>
                                                            <strong>D - Devolucion</strong>
                                                            <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Ingresa al deposito</div>
                                                        </div>
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1, padding: '8px', border: formulario.tipoNotacr === 'C' ? '2px solid #2DCCD3' : '1px solid #ddd', borderRadius: '5px', backgroundColor: formulario.tipoNotacr === 'C' ? '#EDF1FB' : 'white' }}>
                                                        <input type="radio" name="tipoNotacr" value="C"
                                                            checked={formulario.tipoNotacr === 'C'}
                                                            onChange={(e) => setFormulario(f => ({...f, tipoNotacr: e.target.value}))}
                                                            style={{ marginRight: '8px' }} />
                                                        <div>
                                                            <strong>C - Concepto</strong>
                                                            <div style={{ fontSize: '11px', color: '#7f8c8d' }}>Solo descuenta saldo</div>
                                                        </div>
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Comprobante (referencia)</label>
                                                <input type="text" value={formulario.comprobante} onChange={(e) => setFormulario(f => ({...f, comprobante: e.target.value}))} maxLength={30} className="form-input" />
                                            </div>
                                        </div>
                                        <div>
                                            <div className="form-group">
                                                <label>Lista de Precios *</label>
                                                <select value={formulario.idLisprecio || ''} onChange={(e) => setFormulario(f => ({...f, idLisprecio: e.target.value ? Number(e.target.value) : null}))}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                    <option value="">-- Seleccione --</option>
                                                    {listasPrecios.map(lp => (<option key={lp.idLisprecio} value={lp.idLisprecio}>{lp.idLisprecio} - {lp.obs || 'Sin desc'}</option>))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Vendedor</label>
                                                <div className="cuenta-selector" ref={dropdownVendedorRef}>
                                                    {vendedorSeleccionado ? (
                                                        <div className="cuenta-seleccionada">
                                                            <span>{vendedorSeleccionado.idVendedor} - {vendedorSeleccionado.nomVendedor}</span>
                                                            <button type="button" onClick={() => { setVendedorSeleccionado(null); setFormulario(f => ({...f, idVendedor:''})); }} className="btn-limpiar">✕</button>
                                                        </div>
                                                    ) : (
                                                        <input type="text" placeholder="Buscar vendedor..." value={busquedaVendedor} onChange={(e) => setBusquedaVendedor(e.target.value)} onFocus={() => setDropdownVendedorAbierto(true)} className="form-input" />
                                                    )}
                                                    {dropdownVendedorAbierto && !vendedorSeleccionado && (
                                                        <div className="cuenta-dropdown">
                                                            {filtrarVendedores(busquedaVendedor).map(v => (
                                                                <div key={v.idVendedor} className="cuenta-item" onClick={() => seleccionarVendedor(v)}>
                                                                    <strong>{v.idVendedor}</strong> - {v.nomVendedor}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="form-group">
                                                <label>Moneda *</label>
                                                <select value={formulario.idMoneda} onChange={(e) => { const es = e.target.value === 'GS'; setFormulario(f => ({...f, idMoneda: e.target.value, cambio: es ? 1 : 0})); }}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                    <option value="">-- Seleccione --</option>
                                                    {monedas.map(m => (<option key={m.idMoneda} value={m.idMoneda}>{m.idMoneda} - {m.nomMoneda}</option>))}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Tipo de Cambio</label>
                                                <input type="number" value={formulario.cambio} onChange={(e) => setFormulario(f => ({...f, cambio: Number(e.target.value)}))} disabled={formulario.idMoneda === 'GS'}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', backgroundColor: formulario.idMoneda === 'GS' ? '#f0f0f0' : 'white' }} />
                                            </div>
                                            <div className="form-group">
                                                <label>Deposito</label>
                                                <select value={formulario.idDeposito} onChange={(e) => setFormulario(f => ({...f, idDeposito: Number(e.target.value)}))}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                    {depositos.map(d => (<option key={d.idDeposito} value={d.idDeposito}>{d.idDeposito} - {d.nomDepto}</option>))}
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <label>Observaciones</label>
                                        <textarea value={formulario.obs} onChange={(e) => setFormulario(f => ({...f, obs: e.target.value}))} maxLength={60} rows="2" className="form-input" />
                                    </div>
                                </div>

                                {/* Detalle */}
                                <div className="form-section" style={{ marginTop: '20px' }}>
                                    <h3>Items de la Nota de Credito</h3>
                                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div className="form-group">
                                            <label>Producto *</label>
                                            <div className="cuenta-selector" ref={dropdownProductoRef}>
                                                {productoSeleccionado ? (
                                                    <div className="cuenta-seleccionada">
                                                        <span><strong>{productoSeleccionado.id_producto}</strong> - {productoSeleccionado.descripcion}</span>
                                                        <button type="button" onClick={() => { setProductoSeleccionado(null); setBusquedaProducto(''); setDetalleTemp({cantidad:1,precio:0,porcDescuento:0,gravada:0,idPresent:'UN'}); }} className="btn-limpiar">✕</button>
                                                    </div>
                                                ) : (
                                                    <input type="text" placeholder="Buscar producto..." value={busquedaProducto}
                                                        onChange={(e) => { setBusquedaProducto(e.target.value); buscarProductoAPI(e.target.value); }}
                                                        onFocus={() => setDropdownProductoAbierto(true)} className="form-input" />
                                                )}
                                                {dropdownProductoAbierto && !productoSeleccionado && productosEncontrados.length > 0 && (
                                                    <div className="cuenta-dropdown">
                                                        {productosEncontrados.map((p, i) => (
                                                            <div key={i} className="cuenta-item" onClick={() => seleccionarProductoDetalle(p)}>
                                                                <strong>{p.id_producto}</strong> - {p.descripcion} {p.gravada > 0 && <span style={{color:'#7f8c8d',fontSize:'11px'}}>(IVA {p.gravada}%)</span>}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="form-group">
                                            <label>Cantidad</label>
                                            <input type="number" value={detalleTemp.cantidad} min="0" step="1" onChange={(e) => setDetalleTemp(d => ({...d, cantidad: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <label>Precio (c/IVA)</label>
                                            <input type="number" value={detalleTemp.precio} min="0" step="100" onChange={(e) => setDetalleTemp(d => ({...d, precio: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <label>Desc %</label>
                                            <input type="number" value={detalleTemp.porcDescuento} min="0" max="100" step="0.5" onChange={(e) => setDetalleTemp(d => ({...d, porcDescuento: Number(e.target.value)}))} className="form-input" />
                                        </div>
                                        <div className="form-group">
                                            <button type="button" onClick={agregarDetalle} className="btn btn-nuevo" style={{ marginTop: '5px' }} disabled={!productoSeleccionado}>+ Agregar</button>
                                        </div>
                                    </div>

                                    {previewLinea && productoSeleccionado && (
                                        <div style={{ display: 'flex', gap: '20px', padding: '8px 15px', backgroundColor: '#EDF1FB', borderRadius: '6px', marginTop: '5px', fontSize: '13px' }}>
                                            <span>Exenta: <strong>{formatearNumero(previewLinea.monExenta)}</strong></span>
                                            <span>Gravada 5%: <strong>{formatearNumero(previewLinea.monGravada5)}</strong></span>
                                            <span>Gravada 10%: <strong>{formatearNumero(previewLinea.monGravada10)}</strong></span>
                                            <span>IVA: <strong>{formatearNumero(previewLinea.monIva)}</strong></span>
                                            <span style={{ color: '#2DCCD3' }}>Total: <strong>{formatearNumero(previewLinea.total)}</strong></span>
                                        </div>
                                    )}

                                    <div style={{ marginTop: '15px' }}>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead><tr><th>Producto</th><th>Descripcion</th><th style={{textAlign:'right'}}>Cant</th><th style={{textAlign:'right'}}>Precio</th><th style={{textAlign:'right'}}>Desc%</th><th style={{textAlign:'right'}}>Exenta</th><th style={{textAlign:'right'}}>Gravada 5%</th><th style={{textAlign:'right'}}>Gravada 10%</th><th style={{textAlign:'right'}}>IVA</th><th style={{textAlign:'right'}}>Total</th><th>Acc</th></tr></thead>
                                            <tbody>
                                                {detalles.length === 0 ? (
                                                    <tr><td colSpan="11" className="sin-datos">No hay items</td></tr>
                                                ) : detalles.map((d, i) => (
                                                    <tr key={i}>
                                                        <td>{d.idProducto}</td><td>{d.nomProducto}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.cantidad)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.precio)}</td>
                                                        <td style={{textAlign:'right'}}>{d.porcDescuento > 0 ? d.porcDescuento.toFixed(2)+'%' : '0'}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monExenta)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monGravada5)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monGravada10)}</td>
                                                        <td style={{textAlign:'right'}}>{formatearNumero(d.monIva)}</td>
                                                        <td style={{textAlign:'right', fontWeight: 'bold', color: '#001A70'}}>{formatearNumero(d.totalLinea)}</td>
                                                        <td><button type="button" onClick={() => eliminarDetalle(d.idProducto)} className="btn-accion" title="Eliminar">🗑</button></td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                            <tfoot>
                                                <tr style={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                                                    <td colSpan="5" style={{textAlign:'right'}}>TOTALES:</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.exenta)}</td>
                                                    <td colSpan="2" style={{textAlign:'right'}}>{formatearNumero(totales.gravada)}</td>
                                                    <td style={{textAlign:'right'}}>{formatearNumero(totales.iva)}</td>
                                                    <td style={{textAlign:'right', color:'#2DCCD3'}}>{formatearNumero(totales.total)}</td>
                                                    <td></td>
                                                </tr>
                                            </tfoot>
                                        </table>
                                    </div>
                                </div>

                                <div className="form-actions">
                                    <button type="button" onClick={cerrarModal} className="btn-secondary">Cancelar</button>
                                    <button type="submit" className="btn-primary">Guardar</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL VER NCR */}
            {modalVerAbierto && ncreditoVer && (
                <div className="modal-overlay">
                    <div className="modal-contenido-grande" style={{ maxWidth: '1100px' }}>
                        <div className="modal-header">
                            <h3>Nota de Credito Nro {ncreditoVer.notacredito}</h3>
                            <button onClick={() => setModalVerAbierto(false)} className="btn-cerrar">&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="form-section">
                                <h3>Datos de Cabecera</h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', fontSize: '14px' }}>
                                    <div><strong>Sucursal:</strong> {ncreditoVer.idSucursal}</div>
                                    <div><strong>Boca:</strong> {ncreditoVer.boca}</div>
                                    <div><strong>Numero:</strong> {ncreditoVer.notacredito}</div>
                                    <div><strong>Timbrado:</strong> {ncreditoVer.timbrado}</div>
                                    <div><strong>Fecha:</strong> {ncreditoVer.fecha ? formatearFecha(ncreditoVer.fecha) : ''}</div>
                                    <div><strong>Cliente:</strong> {ncreditoVer.nomCliente}</div>
                                    <div><strong>RUC:</strong> {ncreditoVer.rucCliente}</div>
                                    <div><strong>Moneda:</strong> {ncreditoVer.idMoneda}</div>
                                    <div><strong>Cambio:</strong> {ncreditoVer.cambio}</div>
                                    <div><strong>Estado:</strong> {ncreditoVer.estado}</div>
                                    <div><strong>Saldo:</strong> {formatearNumero(ncreditoVer.saldo)}</div>
                                    <div><strong>Comprobante:</strong> {ncreditoVer.comprobante}</div>
                                    <div><strong>Tipo NCR:</strong> {ncreditoVer.tipoNotacr === 'D' ? 'D - Devolucion (ingresa a deposito)' : ncreditoVer.tipoNotacr === 'C' ? 'C - Concepto (solo descuenta saldo)' : ncreditoVer.tipoNotacr || '-'}</div>
                                </div>
                                {ncreditoVer.obs && <div style={{ marginTop: '10px' }}><strong>Obs:</strong> {ncreditoVer.obs}</div>}
                            </div>

                            <div className="form-section" style={{ marginTop: '20px' }}>
                                <h3>Items</h3>
                                <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                    <thead><tr><th>Producto</th><th style={{textAlign:'right'}}>Cant</th><th style={{textAlign:'right'}}>Precio</th><th style={{textAlign:'right'}}>Exenta</th><th style={{textAlign:'right'}}>Gravada</th><th style={{textAlign:'right'}}>IVA</th><th style={{textAlign:'right'}}>Total</th></tr></thead>
                                    <tbody>
                                        {detallesVer.length === 0 ? (
                                            <tr><td colSpan="7" className="sin-datos">Sin items</td></tr>
                                        ) : detallesVer.map((d, i) => (
                                            <tr key={i}>
                                                <td>{d.idProducto}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.cantidad)}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.precio)}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.monExenta)}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.monGravada)}</td>
                                                <td style={{textAlign:'right'}}>{formatearNumero(d.monIva)}</td>
                                                <td style={{textAlign:'right', fontWeight:'bold', color:'#001A70'}}>{formatearNumero(Number(d.monExenta) + Number(d.monGravada) + Number(d.monIva))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '25px', marginTop: '15px', padding: '15px', backgroundColor: '#EDF1FB', borderRadius: '8px', fontSize: '15px' }}>
                                <div><strong>Exenta:</strong> {formatearNumero(ncreditoVer.monExenta)}</div>
                                <div><strong>Gravada:</strong> {formatearNumero(ncreditoVer.monGravada)}</div>
                                <div><strong>IVA:</strong> {formatearNumero(ncreditoVer.monIva)}</div>
                                <div style={{ color: '#001A70', fontSize: '18px' }}><strong>TOTAL: {formatearNumero(ncreditoVer.monTotal)}</strong></div>
                            </div>

                            <div className="form-actions" style={{ marginTop: '15px' }}>
                                <button type="button" onClick={() => setModalVerAbierto(false)} className="btn-primary">Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default FNCredito;
